/**
 * Isometric Character - Generic reusable character with walking animations
 * Loads sprite assets and implements walking animations
 */

import * as PIXI from 'pixi.js';

// Available walk animation directions
const WALK_DIRECTIONS = ['north', 'north-west', 'south', 'south-east'];

// Available rotation directions for static sprites
const ROTATION_DIRECTIONS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'];

/**
 * IsometricCharacter - Generic character with walking animations
 */
export class IsometricCharacter {
  constructor(config) {
    const {
      worldContainer,
      tileWidth,
      tileHeight,
      name = 'Character',
      role = '',
      startGridX = 5,
      startGridY = 5,
      assetBasePath = './assets',
      waypoints = [],
      walkSpeed = 0.03,
      scale = 1.5,
      color = null,
      spriteSheet = null,
      furnitureZone = null,
      furniturePositions = [],
      walkableGrid = null
    } = config;

    this.worldContainer = worldContainer;
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;
    this.name = name;
    this.role = role;
    this.assetBasePath = assetBasePath;
    this.scale = scale;
    this.color = color;
    this.spriteSheet = spriteSheet;
    
    this.walkableGrid = walkableGrid;
    this.gridSize = 7;
    
    this.agentStatus = 'idle';
    this.agentId = null;
    
    this.furnitureZone = furnitureZone;
    this.furniturePositions = furniturePositions;
    
    this.gridX = startGridX;
    this.gridY = startGridY;
    this.targetGridX = startGridX;
    this.targetGridY = startGridY;
    this.screenX = 0;
    this.screenY = 0;
    
    this.isWalking = false;
    this.walkSpeed = walkSpeed;
    this.walkProgress = 0;
    this.startGridX = startGridX;
    this.startGridY = startGridY;
    this.walkDistance = 0;
    
    this.currentDirection = 'south';
    this.currentFrame = 0;
    this.animationSpeed = 0.15;
    this.animationTimer = 0;
    
    this.container = null;
    this.sprite = null;
    this.walkAnimations = {};
    this.nameLabel = null;
    this.statusIndicator = null;
    
    this.waypoints = waypoints.length > 0 ? waypoints : this.generateFullGridWaypoints();
    this.currentWaypointIndex = 0;
    this.waitTime = 0;
    this.minWaitTime = 60;
    this.maxWaitTime = 180;
    
    this.cameraZoom = 1;
    
    this.loaded = false;
    this.loadAssets();
  }

  /**
   * Load sprite assets from the assets folder
   */
  async loadAssets() {
    try {
      // First try to load rotation sprites (Phase 5 assets)
      const rotationsLoaded = await this.loadRotationSprites();
      
      // Then try to load walk animations (Phase 4 assets)
      if (!rotationsLoaded) {
        await this.loadWalkAnimations();
      }
      
      // Create sprite container
      this.createSprite();
      this.loaded = true;
      
      // Set initial position
      this.updateScreenPosition();
      
      console.log(`[${this.name}] Assets loaded successfully`);
    } catch (error) {
      console.error(`[${this.name}] Failed to load assets:`, error);
      // Still mark as loaded to show fallback
      this.createFallbackSprite();
      this.loaded = true;
    }
  }

  /**
   * Load rotation sprites from sprite sheets
   * Sprite sheets have 4 directions: SE, NE, NW, SW (left to right)
   */
  async loadRotationSprites() {
    try {
      this.rotationTextures = {};
      
      // Available character sprite sheets
      const availableSprites = ['human.png', 'human2.png', 'human3.png'];
      
      // Select sprite sheet (use specified one or pick randomly)
      let spriteFile;
      if (this.spriteSheet && availableSprites.includes(this.spriteSheet)) {
        spriteFile = this.spriteSheet;
      } else {
        // Pick a random sprite
        spriteFile = availableSprites[Math.floor(Math.random() * availableSprites.length)];
      }
      
      const spritePath = `./assets/isometric-sprite/${spriteFile}`;
      console.log(`[${this.name}] Loading sprite sheet: ${spriteFile}`);
      
      // Load the sprite sheet
      const texture = await PIXI.Assets.load(spritePath);
      
      if (!texture) {
        console.warn(`[${this.name}] Failed to load sprite sheet: ${spritePath}`);
        return false;
      }
      
      // Extract frames from the sprite sheet
      // Order: SE (0), NE (1), NW (2), SW (3) - left to right
      const frameWidth = texture.width / 4;
      const frameHeight = texture.height;
      
      const spriteDirections = ['south-east', 'north-east', 'north-west', 'south-west'];
      
      for (let i = 0; i < 4; i++) {
        const frame = new PIXI.Rectangle(
          i * frameWidth,
          0,
          frameWidth,
          frameHeight
        );
        const frameTexture = new PIXI.Texture(texture.baseTexture, frame);
        this.rotationTextures[spriteDirections[i]] = frameTexture;
      }
      
      // Map 4 sprite directions to 8 isometric directions
      // SE -> south-east, east, south
      // NE -> north-east, north, east (partial)
      // NW -> north-west, north, west
      // SW -> south-west, south, west (partial)
      this.rotationTextures['east'] = this.rotationTextures['south-east'];
      this.rotationTextures['south'] = this.rotationTextures['south-east'];
      this.rotationTextures['north'] = this.rotationTextures['north-east'];
      this.rotationTextures['west'] = this.rotationTextures['north-west'];
      
      console.log(`[${this.name}] Loaded sprite sheet with ${Object.keys(this.rotationTextures).length} directions`);
      return true;
    } catch (error) {
      console.warn(`[${this.name}] Failed to load sprite sheets:`, error);
      return false;
    }
  }

  /**
   * Load walk animation frames (Phase 4 animated assets)
   */
  async loadWalkAnimations() {
    try {
      // Load metadata
      const metadataResponse = await fetch('./assets/metadata.json');
      const metadata = await metadataResponse.json();
      
      // Load walk animations for each direction
      const loadPromises = [];
      
      for (const direction of WALK_DIRECTIONS) {
        const frames = metadata.frames.animations.walk[direction];
        if (frames) {
          const framePromises = frames.map(framePath => 
            PIXI.Texture.from(`/assets/${framePath}`).baseTexture.resource.load()
              .then(() => PIXI.Texture.from(`/assets/${framePath}`))
          );
          loadPromises.push(
            Promise.all(framePromises).then(textures => {
              this.walkAnimations[direction] = textures;
            })
          );
        }
      }
      
      await Promise.all(loadPromises);
      console.log(`[${this.name}] Loaded walk animations`);
    } catch (error) {
      console.warn(`[${this.name}] Failed to load walk animations:`, error);
    }
  }

  /**
   * Create the sprite container
   */
  createSprite() {
    this.container = new PIXI.Container();
    
    // Use rotation sprites if available (Phase 5 assets)
    if (this.rotationTextures && Object.keys(this.rotationTextures).length > 0) {
      this.createRotationSprite();
    } else if (this.walkAnimations && Object.keys(this.walkAnimations).length > 0) {
      // Fall back to walk animations (Phase 4 assets)
      this.createAnimationSprite();
    } else {
      // Ultimate fallback: emoji avatar
      this.createFallbackSprite();
    }
  }

  /**
   * Create sprite using rotation sprites (Phase 5 pixel art)
   */
  createRotationSprite() {
    // Start with south-facing sprite
    const texture = this.rotationTextures['south'] || Object.values(this.rotationTextures)[0];

    if (texture) {
      this.sprite = new PIXI.Sprite(texture);
      this.sprite.anchor.set(0.5, 0.9); // Anchor near bottom
      this.sprite.scale.set(this.scale * 0.4);

      this.container.addChild(this.sprite);
    }

    this.addLabelsAndIndicators();
  }

  /**
   * Create sprite using walk animations (Phase 4 animated)
   */
  createAnimationSprite() {
    const initialDirection = WALK_DIRECTIONS[0];
    const textures = this.walkAnimations[initialDirection];

    if (textures && textures.length > 0) {
      this.sprite = new PIXI.Sprite(textures[0]);
      this.sprite.anchor.set(0.5, 1); // Anchor at bottom center
      this.sprite.scale.set(this.scale * 0.4);

      this.container.addChild(this.sprite);
    }

    this.addLabelsAndIndicators();
  }

  /**
   * Create fallback emoji-based avatar
   */
  createFallbackSprite() {
    // Create a circle background
    const graphics = new PIXI.Graphics();
    graphics.beginFill(0x4299e1);
    graphics.drawCircle(0, -20, 20);
    graphics.endFill();
    graphics.lineStyle(2, 0xffffff, 0.8);
    graphics.drawCircle(0, -20, 20);
    
    // Add emoji text
    const emojiText = new PIXI.Text('👤', {
      fontFamily: 'Arial',
      fontSize: 20,
      align: 'center'
    });
    emojiText.anchor.set(0.5);
    emojiText.position.set(0, -20);
    
    this.sprite = new PIXI.Container();
    this.sprite.addChild(graphics);
    this.sprite.addChild(emojiText);
    
    this.container.addChild(this.sprite);
    this.addLabelsAndIndicators();
  }

  /**
   * Add name labels and status indicators
   */
  addLabelsAndIndicators() {
    const labelText = this.role ? `${this.name} (${this.role})` : this.name;
    const nameLabel = new PIXI.Text(labelText, {
      fontFamily: 'Arial',
      fontSize: 20,
      fill: 0xffffff,
      align: 'center',
      dropShadow: true,
      dropShadowColor: 0x000000,
      dropShadowDistance: 2,
      dropShadowBlur: 4
    });
    nameLabel.anchor.set(0.5);
    nameLabel.position.set(0, -70 * (this.scale / 1.5));
    nameLabel.scale.set(0.5);
    this.nameLabel = nameLabel;
    this.container.addChild(nameLabel);
    
    // Status indicator dot (6px radius) - positioned to the right of the name
    const statusIndicator = new PIXI.Graphics();
    this.drawStatusDot(statusIndicator, this.agentStatus);
    statusIndicator.position.set(nameLabel.width / 2 + 8, -70 * (this.scale / 1.5));
    this.statusIndicator = statusIndicator;
    this.container.addChild(statusIndicator);
    
    this.worldContainer.addChild(this.container);
  }

  /**
   * Draw status dot with appropriate color
   */
  drawStatusDot(graphics, status) {
    const statusColors = {
      idle: 0xffa500,    // Orange
      active: 0x48bb78,  // Green
      blocked: 0xe53e3e  // Red
    };
    
    graphics.clear();
    graphics.beginFill(statusColors[status] || statusColors.idle);
    graphics.drawCircle(0, 0, 3);
    graphics.endFill();
  }

  isTileWalkable(x, y) {
    if (this.walkableGrid) {
      return this.walkableGrid.isWalkable(x, y);
    }
    return true;
  }

  /**
   * Update agent status - just updates the status indicator color
   */
  updateAgentStatus(agentData) {
    this.agentStatus = agentData.status || 'idle';
    this.agentId = agentData._id;
    
    // Update status dot color
    if (this.statusIndicator) {
      this.drawStatusDot(this.statusIndicator, this.agentStatus);
    }
    
    console.log(`[${this.name}] Status changed to ${this.agentStatus}`);
  }

  generateFullGridWaypoints() {
    if (this.walkableGrid) {
      return this.walkableGrid.getWalkableTiles().sort(() => Math.random() - 0.5);
    }
    
    const waypoints = [];
    for (let x = 0; x < this.gridSize; x++) {
      for (let y = 0; y < this.gridSize; y++) {
        waypoints.push({ x, y });
      }
    }
    return waypoints.sort(() => Math.random() - 0.5);
  }

  /**
   * Generate waypoints based on furniture zone (for initialization only)
   */
  generateZoneWaypoints() {
    if (this.furniturePositions && this.furniturePositions.length > 0) {
      const zoneWaypoints = [];
      
      for (const furniture of this.furniturePositions) {
        zoneWaypoints.push({ x: furniture.x, y: furniture.y });
        
        for (let dx = -this.nearFurnitureRadius; dx <= this.nearFurnitureRadius; dx++) {
          for (let dy = -this.nearFurnitureRadius; dy <= this.nearFurnitureRadius; dy++) {
            if (dx === 0 && dy === 0) continue;
            
            const x = furniture.x + dx;
            const y = furniture.y + dy;
            
            if (x >= 0 && x < this.gridSize && y >= 0 && y < this.gridSize && this.isTileWalkable(x, y)) {
              zoneWaypoints.push({ x, y });
            }
          }
        }
      }
      
      const uniqueWaypoints = [];
      const seen = new Set();
      for (const wp of zoneWaypoints) {
        const key = `${wp.x},${wp.y}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueWaypoints.push(wp);
        }
      }
      
      return uniqueWaypoints.sort(() => Math.random() - 0.5);
    }
    
    return this.generateFullGridWaypoints();
  }

  /**
   * Get next waypoint based on current status
   */
  getNextWaypoint() {
    // When idle, use full grid waypoints
    if (this.agentStatus === 'idle') {
      // Ensure we have full grid waypoints
      if (this.waypoints.length < 50) {
        this.waypoints = this.generateFullGridWaypoints();
        this.currentWaypointIndex = 0;
      }
      
      const waypoint = this.waypoints[this.currentWaypointIndex];
      this.currentWaypointIndex = (this.currentWaypointIndex + 1) % this.waypoints.length;
      return waypoint;
    }
    
    // When active or blocked, stay at current position (shouldn't reach here when stationary)
    return { x: Math.round(this.gridX), y: Math.round(this.gridY) };
  }

  /**
   * Calculate direction based on movement vector
   */
  calculateDirection(dx, dy) {
    // Calculate angle in radians
    const angle = Math.atan2(dy, dx);
    const degrees = angle * (180 / Math.PI);
    
    // Map angle to 8 directions
    // 0° = East, 90° = South, 180° = West, -90° = North
    // Adjust to match isometric grid (rotated 45°)
    const adjustedDegrees = degrees + 45;
    const normalizedDegrees = ((adjustedDegrees % 360) + 360) % 360;
    
    // Determine direction based on angle
    if (normalizedDegrees >= 337.5 || normalizedDegrees < 22.5) {
      return 'south-east';
    } else if (normalizedDegrees >= 22.5 && normalizedDegrees < 67.5) {
      return 'south';
    } else if (normalizedDegrees >= 67.5 && normalizedDegrees < 112.5) {
      return 'south-west';
    } else if (normalizedDegrees >= 112.5 && normalizedDegrees < 157.5) {
      return 'west';
    } else if (normalizedDegrees >= 157.5 && normalizedDegrees < 202.5) {
      return 'north-west';
    } else if (normalizedDegrees >= 202.5 && normalizedDegrees < 247.5) {
      return 'north';
    } else if (normalizedDegrees >= 247.5 && normalizedDegrees < 292.5) {
      return 'north-east';
    } else {
      return 'east';
    }
  }

  /**
   * Get closest available animation direction
   * Maps 8 directions to available assets
   */
  getAnimationDirection(direction) {
    // If we have rotation textures, use the exact direction
    if (this.rotationTextures && this.rotationTextures[direction]) {
      return direction;
    }
    
    // Map to available walk animations (only 4 directions)
    const directionMap = {
      'north': 'north',
      'north-west': 'north-west',
      'west': 'north-west',
      'south-west': 'south',
      'south': 'south',
      'south-east': 'south-east',
      'east': 'south-east',
      'north-east': 'north'
    };
    return directionMap[direction] || 'south';
  }

  /**
   * Convert grid to screen coordinates
   */
  gridToScreen(gridX, gridY) {
    const screenX = (gridX - gridY) * (this.tileWidth / 2);
    const screenY = (gridX + gridY) * (this.tileHeight / 2);
    return { x: screenX, y: screenY };
  }

  /**
   * Update screen position based on grid position
   */
  updateScreenPosition() {
    const pos = this.gridToScreen(this.gridX, this.gridY);
    this.screenX = pos.x;
    this.screenY = pos.y;
    
    if (this.container) {
      this.container.position.set(this.screenX, this.screenY);
      // Mise à jour du zIndex : personnages entre meubles (zOffset=0) et chaises (zOffset=2)
      // zOffset = 1 pour les personnages
      this.container.zIndex = Math.round((this.gridX + this.gridY) * 10 + 1);
    }
  }

  /**
   * Check if a straight-line path between two points crosses any obstacle tile
   */
  pathCrossesObstacle(fromX, fromY, toX, toY) {
    const currentX = Math.round(fromX);
    const currentY = Math.round(fromY);
    
    const dx = toX - fromX;
    const dy = toY - fromY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const steps = Math.ceil(dist * 4);
    
    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      const cx = Math.round(fromX + dx * t);
      const cy = Math.round(fromY + dy * t);
      if ((cx !== currentX || cy !== currentY) && !this.isTileWalkable(cx, cy)) {
        return true;
      }
    }
    
    const destX = Math.round(toX);
    const destY = Math.round(toY);
    if ((destX !== currentX || destY !== currentY) && !this.isTileWalkable(destX, destY)) {
      return true;
    }
    return false;
  }

  startWalking(targetX, targetY) {
    targetX = Math.round(targetX);
    targetY = Math.round(targetY);
    
    if (!this.isTileWalkable(targetX, targetY)) {
      this.waitTime = 1;
      return;
    }
    
    if (this.pathCrossesObstacle(this.gridX, this.gridY, targetX, targetY)) {
      const path = this.findPathToTarget(targetX, targetY);
      if (path && path.length > 1) {
        targetX = path[1].x;
        targetY = path[1].y;
      } else {
        this.waitTime = 1;
        return;
      }
    }
    
    this.startGridX = this.gridX;
    this.startGridY = this.gridY;
    this.targetGridX = targetX;
    this.targetGridY = targetY;
    this.isWalking = true;
    this.walkProgress = 0;
    
    const dx = targetX - this.gridX;
    const dy = targetY - this.gridY;
    this.walkDistance = Math.sqrt(dx * dx + dy * dy);
    
    const direction = this.calculateDirection(dx, dy);
    this.currentDirection = this.getAnimationDirection(direction);
  }

  findPathToTarget(targetX, targetY) {
    const fromX = Math.round(this.gridX);
    const fromY = Math.round(this.gridY);
    
    if (this.walkableGrid) {
      return this.walkableGrid.findPath(fromX, fromY, targetX, targetY);
    }
    
    return this.findDetourWaypoint(targetX, targetY) ? [fromX, fromY, this.findDetourWaypoint(targetX, targetY)] : null;
  }

  findDetourWaypoint(targetX, targetY) {
    const fromX = Math.round(this.gridX);
    const fromY = Math.round(this.gridY);
    
    const offsets = [
      { dx: 1, dy: 0 }, { dx: -1, dy: 0 },
      { dx: 0, dy: 1 }, { dx: 0, dy: -1 },
      { dx: 1, dy: 1 }, { dx: -1, dy: -1 },
      { dx: 1, dy: -1 }, { dx: -1, dy: 1 }
    ];
    
    let bestWaypoint = null;
    let bestDist = Infinity;
    
    for (const off of offsets) {
      const nx = fromX + off.dx;
      const ny = fromY + off.dy;
      
      if (nx < 0 || nx >= this.gridSize || ny < 0 || ny >= this.gridSize) continue;
      if (!this.isTileWalkable(nx, ny)) continue;
      
      if (this.pathCrossesObstacle(this.gridX, this.gridY, nx, ny)) continue;
      
      const dist = Math.abs(nx - targetX) + Math.abs(ny - targetY);
      if (dist < bestDist) {
        bestDist = dist;
        bestWaypoint = { x: nx, y: ny };
      }
    }
    
    return bestWaypoint;
  }

  findAdjacentToDest(deskX, deskY) {
    const offsets = [
      { dx: 0, dy: 1 },  { dx: 1, dy: 0 },
      { dx: 0, dy: -1 }, { dx: -1, dy: 0 },
      { dx: 1, dy: 1 },  { dx: -1, dy: -1 },
      { dx: 1, dy: -1 }, { dx: -1, dy: 1 }
    ];
    
    let bestTile = null;
    let bestDist = Infinity;
    
    for (const off of offsets) {
      const nx = deskX + off.dx;
      const ny = deskY + off.dy;
      
      if (nx < 0 || nx >= this.gridSize || ny < 0 || ny >= this.gridSize) continue;
      if (!this.isTileWalkable(nx, ny)) continue;
      
      const dist = Math.abs(nx - this.gridX) + Math.abs(ny - this.gridY);
      if (dist < bestDist) {
        bestDist = dist;
        bestTile = { x: nx, y: ny };
      }
    }
    
    return bestTile;
  }

  /**
   * Update walking animation
   */
  updateWalking(delta) {
    if (!this.isWalking) return;
    
    // Update walk progress - adjust speed by distance for smooth constant visual speed
    // If distance is 2 tiles, progress is half as fast, so it takes twice as long
    const adjustedSpeed = this.walkSpeed / Math.max(this.walkDistance, 0.5);
    this.walkProgress += adjustedSpeed * delta;
    
    if (this.walkProgress >= 1) {
      // Arrived at destination
      this.gridX = this.targetGridX;
      this.gridY = this.targetGridY;
      this.isWalking = false;
      this.walkProgress = 0;
      this.updateScreenPosition();
      
      // Set wait time before next move
      this.waitTime = this.minWaitTime + Math.random() * (this.maxWaitTime - this.minWaitTime);
      
      // Reset to first frame
      this.currentFrame = 0;
      this.updateSpriteFrame();
    } else {
      // Interpolate position from stored start position
      const currentX = this.startGridX + (this.targetGridX - this.startGridX) * this.walkProgress;
      const currentY = this.startGridY + (this.targetGridY - this.startGridY) * this.walkProgress;
      
      // Update grid position to current interpolated position
      this.gridX = currentX;
      this.gridY = currentY;
      
      const pos = this.gridToScreen(currentX, currentY);
      this.screenX = pos.x;
      this.screenY = pos.y;
      
      if (this.container) {
        this.container.position.set(this.screenX, this.screenY);
        // Mise à jour du zIndex pendant le mouvement (zOffset = 1 pour personnages)
        this.container.zIndex = Math.round((this.gridX + this.gridY) * 10 + 1);
      }
    }
  }

  /**
   * Update animation frame
   */
  updateAnimation(delta) {
    if (!this.isWalking) return;
    
    this.animationTimer += delta * this.animationSpeed;
    
    if (this.animationTimer >= 1) {
      this.animationTimer = 0;
      
      // Advance frame
      const textures = this.walkAnimations[this.currentDirection];
      if (textures) {
        this.currentFrame = (this.currentFrame + 1) % textures.length;
        this.updateSpriteFrame();
      }
    }
  }

  /**
   * Update sprite texture to current frame
   */
  updateSpriteFrame() {
    if (!this.sprite) return;
    
    // If using rotation sprites, update direction
    if (this.rotationTextures && this.rotationTextures[this.currentDirection]) {
      this.sprite.texture = this.rotationTextures[this.currentDirection];
    }
    // Otherwise use walk animation frames
    else if (this.walkAnimations[this.currentDirection]) {
      const textures = this.walkAnimations[this.currentDirection];
      if (textures && textures[this.currentFrame]) {
        this.sprite.texture = textures[this.currentFrame];
      }
    }
  }

  /**
   * Main update loop
   */
  update(delta) {
    if (!this.loaded) return;
    
    // Always update bubbles
    this.updateBubbles(delta);
    
    if (this.isWalking) {
      this.updateWalking(delta);
      this.updateAnimation(delta);
    } else {
      // Waiting at waypoint
      if (this.waitTime > 0) {
        this.waitTime -= delta;
      } else {
        // Pick next waypoint and start walking
        const nextWaypoint = this.getNextWaypoint();
        this.startWalking(nextWaypoint.x, nextWaypoint.y);
      }
    }
  }

  /**
   * Show a pixel-art speech bubble above the character
   */
  showBubble(message) {
    if (!this.container) return;

    const bubble = new PIXI.Container();
    bubble.position.set(0, -80 * (this.scale / 1.5));

    // Create text first to measure it — no truncation, bubble expands
    const msgText = new PIXI.Text(message, {
      fontFamily: 'Arial, Helvetica, sans-serif',
      fontSize: 10,
      fill: 0x222222,
      wordWrap: true,
      wordWrapWidth: 200,
      lineHeight: 14
    });
    msgText.anchor.set(0.5, 0.5);

    const padX = 10;
    const padY = 8;
    const bw = msgText.width + padX * 2;
    const bh = msgText.height + padY * 2;

    // Pixel-art bubble background
    const bg = new PIXI.Graphics();

    // Shadow (offset pixel-art style)
    bg.beginFill(0x000000, 0.2);
    bg.drawRect(-bw / 2 + 2, -bh / 2 + 2, bw, bh);
    bg.endFill();

    // Main bubble body (white)
    bg.beginFill(0xffffff);
    bg.lineStyle(2, 0x333333, 1);
    bg.drawRect(-bw / 2, -bh / 2, bw, bh);
    bg.endFill();

    // Pixel-art tail (small triangle pointing down)
    bg.beginFill(0xffffff);
    bg.lineStyle(2, 0x333333, 1);
    bg.moveTo(-4, bh / 2);
    bg.lineTo(0, bh / 2 + 8);
    bg.lineTo(4, bh / 2);
    bg.endFill();
    // Fill the gap where tail meets bubble
    bg.lineStyle(0);
    bg.beginFill(0xffffff);
    bg.drawRect(-3, bh / 2 - 1, 6, 2);
    bg.endFill();

    bubble.addChild(bg);
    bubble.addChild(msgText);

    // Animation state
    bubble._floatSpeed = 0.3;
    bubble._fadeDelay = 360;  // frames before starting to fade (6 seconds at 60fps)
    bubble._fadeDuration = 60; // frames to fade out (1 second)
    bubble._age = 0;
    bubble._startY = bubble.position.y;

    this.container.addChild(bubble);

    // Store active bubbles
    if (!this.activeBubbles) this.activeBubbles = [];
    this.activeBubbles.push(bubble);

    // If too many bubbles, remove oldest
    if (this.activeBubbles.length > 3) {
      const old = this.activeBubbles.shift();
      if (old.parent) old.parent.removeChild(old);
      old.destroy({ children: true });
    }
  }

  /**
   * Set camera zoom for scaling bubbles
   */
  setCameraZoom(zoom) {
    this.cameraZoom = zoom;
  }

  /**
   * Update active speech bubbles (float up + fade out)
   */
  updateBubbles(delta) {
    if (!this.activeBubbles || this.activeBubbles.length === 0) return;

    const toRemove = [];

    for (const bubble of this.activeBubbles) {
      bubble._age += delta;

      // Float upward slowly
      bubble.position.y = bubble._startY - (bubble._age * bubble._floatSpeed);
      
      // Scale bubble to counter camera zoom (keep constant size on screen)
      const inverseZoom = 1 / Math.max(this.cameraZoom, 0.1);
      bubble.scale.set(inverseZoom);

      // Fade out after delay
      if (bubble._age > bubble._fadeDelay) {
        const fadeProgress = (bubble._age - bubble._fadeDelay) / bubble._fadeDuration;
        bubble.alpha = Math.max(0, 1 - fadeProgress);

        if (fadeProgress >= 1) {
          toRemove.push(bubble);
        }
      }
    }

    // Clean up finished bubbles
    for (const bubble of toRemove) {
      const idx = this.activeBubbles.indexOf(bubble);
      if (idx !== -1) this.activeBubbles.splice(idx, 1);
      if (bubble.parent) bubble.parent.removeChild(bubble);
      bubble.destroy({ children: true });
    }
  }

  /**
   * Destroy the character
   */
  destroy() {
    if (this.container) {
      this.container.destroy({ children: true });
    }
    this.walkAnimations = {};
    this.activeBubbles = [];
  }
}

export default IsometricCharacter;

